import { t, getLocale } from '/src/i18n.js';

class TransactionLedger extends HTMLElement {
 
    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow    = this.attachShadow({ mode: "open" });
      this._movements = [];
      this._sortKey   = "date";
      this._sortDir   = "desc";
      this._query     = "";
      this._typeFilter = "all";
      this._dateFrom  = "";
      this._dateTo    = "";
    }
 
    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._render();
      this._bindEvents();
    }
 
    // ── C. JS property setter ────────────────────────────────────────────
    // Accepts array of { id, date, type, description, amount }
    // type: "expense" | "payment"
    // amount: negative for expenses, positive for payments
    set movements(data) {
      this._movements = Array.isArray(data) ? data : [];
      this._renderList();
      this._renderSummaries();
    }
 
    // ── D. Attribute → property bridge ───────────────────────────────────
    static get observedAttributes() { return ["movements"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "movements") {
        try { this.movements = JSON.parse(newVal); } catch {}
      }
    }
 
    // ── E. Format helpers ────────────────────────────────────────────────
    _fmtAmount(v) {
      const sign = v < 0 ? "\u2212" : "+";
      return sign + "CHF\u00A0" + Math.abs(v).toLocaleString("de-CH", {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    }
 
    _fmtDate(d) {
      const locale = getLocale();
      return new Date(d + "T00:00:00").toLocaleDateString(locale, {
        day: "numeric", month: "short", year: "numeric"
      });
    }
 
    // ── F. SVG icons ──────────────────────────────────────────────────────
    _expenseIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 3v10M5 10l3 3 3-3" stroke="currentColor" stroke-width="1.3"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
 
    _paymentIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 13V3M5 6l3-3 3 3" stroke="currentColor" stroke-width="1.3"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
 
    _sortIconUp() {
      return `<svg class="sort-icon" viewBox="0 0 12 12" fill="none">
        <path d="M6 10V2M3 5l3-3 3 3" stroke="currentColor" stroke-width="1.2"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
 
    _sortIconDown() {
      return `<svg class="sort-icon" viewBox="0 0 12 12" fill="none">
        <path d="M6 2v8M3 7l3 3 3-3" stroke="currentColor" stroke-width="1.2"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }
 
    // ── G. Get filtered + sorted subset ──────────────────────────────────
    _getRows() {
      return this._movements
        .filter(r => {
          if (this._query && !r.description.toLowerCase().includes(this._query)) return false;
          if (this._typeFilter !== "all" && r.type !== this._typeFilter) return false;
          if (this._dateFrom && r.date < this._dateFrom) return false;
          if (this._dateTo   && r.date > this._dateTo)   return false;
          return true;
        })
        .sort((a, b) => {
          const va = this._sortKey === "date" ? a.date : Math.abs(a.amount);
          const vb = this._sortKey === "date" ? b.date : Math.abs(b.amount);
          if (va < vb) return this._sortDir === "asc" ? -1 :  1;
          if (va > vb) return this._sortDir === "asc" ?  1 : -1;
          return 0;
        });
    }
 
    // ── H. Render summary metric cards ────────────────────────────────────
    _renderSummaries() {
      const rows    = this._getRows();
      const el      = this._shadow.getElementById("summaries");
      if (!el) return;
 
      const expenses = rows.filter(r => r.type === "expense").reduce((s, r) => s + r.amount, 0);
      const payments = rows.filter(r => r.type === "payment").reduce((s, r) => s + r.amount, 0);
      const balance  = rows.reduce((s, r) => s + r.amount, 0);
 
      el.innerHTML = `
        <div class="metric">
          <div class="metric-label">${t("expenses")}</div>
          <div class="metric-val expense">${this._fmtAmount(expenses)}</div>
        </div>
        <div class="metric">
          <div class="metric-label">${t("payments")}</div>
          <div class="metric-val payment">${this._fmtAmount(payments)}</div>
        </div>
        <div class="metric">
          <div class="metric-label">${t("balance")}</div>
          <div class="metric-val ${balance >= 0 ? 'payment' : 'expense'}">${this._fmtAmount(balance)}</div>
        </div>`;
    }
 
    // ── I. Render the transaction rows ────────────────────────────────────
    _renderList() {
      const rows  = this._getRows();
      const listEl = this._shadow.getElementById("list");
      const countEl = this._shadow.getElementById("count");
      const balanceEl = this._shadow.getElementById("balance");
      if (!listEl) return;
 
      if (!rows.length) {
        listEl.innerHTML = `<div class="empty">${t("no-transactions")}</div>`;
      } else {
        listEl.innerHTML = rows.map(r => {
          const icon   = r.type === "expense" ? this._expenseIcon() : this._paymentIcon();
          const amtStr = this._fmtAmount(r.amount);
          return `
            <div class="row" data-id="${r.id}" role="row" tabindex="0"
                 aria-label="${r.type}: ${r.description}, ${amtStr}, ${this._fmtDate(r.date)}">
              <div class="icon-cell">
                <div class="icon-bubble ${r.type}">${icon}</div>
              </div>
              <div>
                <div class="date">${this._fmtDate(r.date)}</div>
                <!--<span class="type-pill ${r.type}">${r.type}</span>-->
              </div>
              <div class="desc" title="${r.description}">${r.description}</div>
              <div class="amount-cell">
                <div class="amount ${r.type}">${amtStr}</div>
              </div>
            </div>`;
        }).join("");
 
        // Row click/keydown events
        listEl.querySelectorAll(".row").forEach(el => {
          el.addEventListener("click", () => this._dispatchSelect(Number(el.dataset.id)));
          el.addEventListener("keydown", e => {
            if (e.key === "Enter" || e.key === " ") this._dispatchSelect(Number(el.dataset.id));
          });
        });
      }
 
      const n       = rows.length;
      const balance = rows.reduce((s, r) => s + r.amount, 0);
      const transactionLabel = n === 1 ? t("transaction-sg") : t("transaction-pl");
      if (countEl)   countEl.textContent   = n + " " + transactionLabel;
      if (balanceEl) balanceEl.textContent = t("net") + " " + this._fmtAmount(balance);
 
      this._renderSummaries();
    }
 
    // ── J. Fire ledger-select custom event ────────────────────────────────
    _dispatchSelect(id) {
      const movement = this._movements.find(m => m.id === id);
      if (movement) {
        this.dispatchEvent(new CustomEvent("ledger-select", {
          detail: movement, bubbles: true, composed: true
        }));
      }
    }
 
    // ── K. Sort by column key, toggle direction on re-click ───────────────
    _handleSort(key) {
      if (this._sortKey === key) {
        this._sortDir = this._sortDir === "asc" ? "desc" : "asc";
      } else {
        this._sortKey = key;
        this._sortDir = "desc";
      }
      this._updateSortHeaders();
      this._renderList();
    }
 
    // ── L. Refresh column header icons to reflect current sort state ──────
    _updateSortHeaders() {
      const dateBtn   = this._shadow.getElementById("col-date");
      const amountBtn = this._shadow.getElementById("col-amount");
      if (!dateBtn || !amountBtn) return;
 
      const activeIcon   = this._sortDir === "asc" ? this._sortIconUp()   : this._sortIconDown();
      const inactiveIcon = this._sortIconDown();
 
      dateBtn.classList.toggle("active", this._sortKey === "date");
      amountBtn.classList.toggle("active", this._sortKey === "amount");
 
      dateBtn.innerHTML   = t("date") + " " + (this._sortKey === "date"   ? activeIcon : inactiveIcon);
      amountBtn.innerHTML = t("amount") + " " + (this._sortKey === "amount" ? activeIcon : inactiveIcon);
    }
 
    // ── M. Bind toolbar + column header events ────────────────────────────
    _bindEvents() {
      const root = this._shadow;
 
      root.getElementById("search").addEventListener("input", e => {
        this._query = e.target.value.toLowerCase();
        this._renderList();
      });
 
      root.getElementById("type-filter").addEventListener("change", e => {
        this._typeFilter = e.target.value;
        this._renderList();
      });
 
      root.getElementById("date-from").addEventListener("change", e => {
        this._dateFrom = e.target.value;
        this._renderList();
      });
 
      root.getElementById("date-to").addEventListener("change", e => {
        this._dateTo = e.target.value;
        this._renderList();
      });
 
      root.getElementById("col-date").addEventListener("click", () => this._handleSort("date"));
      root.getElementById("col-amount").addEventListener("click", () => this._handleSort("amount"));
    }
 
    // ── N. Full initial render (shadow DOM shell + styles) ────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          :host { display: block; font-family: system-ui, sans-serif; }
 
          .wrap {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            overflow: hidden;
            /* max-width: 700px; */
          }
 
          .topbar { padding: 14px 16px 0; }
 
          .title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
            gap: 12px;
            flex-wrap: wrap;
          }
 
          .title {
            font-size: 15px;
            font-weight: 500;
            color: #1a1a1a;
            display: flex;
            align-items: center;
            gap: 8px;
          }
 
          .title-icon {
            width: 28px; height: 28px;
            background: #5D4037;
            border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
 
          .summaries {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            margin-bottom: 14px;
          }
 
          .metric { background: #f5f5f3; border-radius: 8px; padding: 10px 12px; }
          .metric-label { font-size: 11px; color: #aaa; margin-bottom: 3px; }
          .metric-val { font-size: 18px; font-weight: 500; line-height: 1; color: #1a1a1a; }
          .metric-val.expense { color: #A32D2D; }
          .metric-val.payment { color: #0F6E56; }
 
          .toolbar {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            padding-bottom: 12px;
            border-bottom: 0.5px solid #e0dfd7;
          }
 
          input, select {
            font: inherit;
            font-size: 13px;
            padding: 7px 10px;
            border: 0.5px solid #d0cfc7;
            border-radius: 8px;
            background: #fff;
            outline: none;
            color: #1a1a1a;
          }
 
          input[type="text"] { flex: 1; min-width: 120px; }
          input:focus, select:focus { border-color: #888; }
 
          .col-header {
            display: grid;
            grid-template-columns: 36px 90px 1fr 110px;
            gap: 0 12px;
            align-items: center;
            padding: 8px 16px;
            border-bottom: 0.5px solid #e0dfd7;
          }
 
          .col-btn {
            background: none;
            border: none;
            font: inherit;
            font-size: 11px;
            font-weight: 500;
            color: #aaa;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 0;
            text-align: left;
            white-space: nowrap;
          }
 
          .col-btn:hover { color: #1a1a1a; }
          .col-btn.active { color: #1a1a1a; }
 
          .sort-icon { width: 12px; height: 12px; flex-shrink: 0; opacity: 0.35; }
          .col-btn.active .sort-icon { opacity: 1; }
 
          .col-desc   { font-size: 11px; font-weight: 500; color: #aaa; }
          .col-amount { font-size: 11px; font-weight: 500; color: #aaa; text-align: right; }
 
          .list { display: flex; flex-direction: column; }
 
          .row {
            display: grid;
            grid-template-columns: 36px 90px 1fr 110px;
            gap: 0 12px;
            align-items: center;
            padding: 11px 16px;
            border-bottom: 0.5px solid #e0dfd7;
            cursor: pointer;
            transition: background 0.1s;
          }
 
          .row:last-child { border-bottom: none; }
          .row:hover { background: #f7f6f2; }
          .row:focus-visible { outline: 2px solid #4a90d9; outline-offset: -2px; }
 
          .icon-cell {
            display: flex;
            align-items: center;
            justify-content: center;
          }
 
          .icon-bubble {
            width: 32px; height: 32px;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
 
          .icon-bubble.expense { background: #FCEBEB; color: #A32D2D; }
          .icon-bubble.payment { background: #E1F5EE; color: #0F6E56; }
 
          .date { font-size: 12px; color: #888; white-space: nowrap; }
 
          .type-pill {
            display: inline-flex;
            align-items: center;
            font-size: 10px;
            font-weight: 500;
            padding: 2px 6px;
            border-radius: 99px;
            margin-top: 2px;
          }
 
          .type-pill.expense { background: #FCEBEB; color: #A32D2D; }
          .type-pill.payment { background: #E1F5EE; color: #0F6E56; }
 
          .desc {
            font-size: 13px;
            color: #1a1a1a;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
 
          .amount-cell { text-align: right; }
 
          .amount {
            font-size: 14px;
            font-weight: 500;
            font-variant-numeric: tabular-nums;
            letter-spacing: -0.02em;
          }
 
          .amount.expense { color: #A32D2D; }
          .amount.payment { color: #0F6E56; }
 
          .empty {
            padding: 2.5rem 1rem;
            text-align: center;
            font-size: 13px;
            color: #aaa;
          }
 
          .footer {
            padding: 10px 16px;
            border-top: 0.5px solid #e0dfd7;
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 8px;
          }
 
          .count   { font-size: 12px; color: #aaa; }
          .balance { font-size: 13px; font-weight: 500; color: #1a1a1a; }
 
          @media (max-width: 480px) {
            .col-header, .row {
              grid-template-columns: 32px 76px 1fr 90px;
              gap: 0 8px;
              padding-left: 12px;
              padding-right: 12px;
            }
            .summaries { grid-template-columns: repeat(2, minmax(0,1fr)); }
            .metric-val { font-size: 15px; }
          }
 
          /*
          @media (prefers-color-scheme: dark) {
            .wrap { background: #1e1e1c; border-color: #333330; }
            .title { color: #f0ede8; }
            .metric { background: #2a2a28; }
            .metric-val { color: #f0ede8; }
            input, select { background: #2a2a28; border-color: #444441; color: #f0ede8; }
            .col-btn:hover, .col-btn.active { color: #f0ede8; }
            .col-header { border-bottom-color: #333330; }
            .row { border-bottom-color: #333330; }
            .row:hover { background: #2a2a28; }
            .desc { color: #f0ede8; }
            .balance { color: #f0ede8; }
            .toolbar { border-bottom-color: #333330; }
            .footer { border-top-color: #333330; }
          }
          */
        </style>
 
        <div class="wrap">
          <div class="topbar">
            <div class="title-row">
                <div class="title">
                  <div class="title-icon">
                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                      <rect x="2" y="2" width="14" height="14" rx="2" stroke="#F5EDEB" stroke-width="1.3"/>
                      <path d="M5 6h8M5 9h8M5 12h5" stroke="#F5EDEB" stroke-width="1.3" stroke-linecap="round"/>
                    </svg>
                  </div>
                  ${t("ledger")}
                </div>
            </div>
 
            <div class="summaries" id="summaries"></div>
 
            <div class="toolbar">
              <input type="text" id="search" placeholder="${t("search-description")}" />
              <select id="type-filter">
                <option value="all">${t("all-types")}</option>
                <option value="expense">${t("expenses-only")}</option>
                <option value="payment">${t("payments-only")}</option>
              </select>
              <input type="date" id="date-from" title="${t("from-date")}" aria-label="${t("from-date")}" />
              <input type="date" id="date-to"   title="${t("to-date")}" aria-label="${t("to-date")}" />
            </div>
          </div>
 
            <div class="col-header" role="row">
              <div></div>
              <button class="col-btn active" id="col-date" aria-label="${t("sort-by-date")}">
                ${t("date")} ${this._sortIconDown()}
              </button>
              <div class="col-desc">${t("description")}</div>
              <button class="col-btn" id="col-amount" aria-label="${t("sort-by-amount")}"
                      style="justify-content:flex-end">
                ${t("amount")} ${this._sortIconDown()}
              </button>
            </div>
 
          <div class="list" id="list" role="table" aria-label="Transaction list"></div>
 
          <div class="footer">
            <span class="count"   id="count"></span>
            <span class="balance" id="balance"></span>
          </div>
        </div>
      `;
    }
  }

export { TransactionLedger }